import { useState } from 'react';
import type { Track } from '@/domain/types';
import { command } from '@/commands/registry';
import * as SamplerCommands from '@/commands/samplerCommands';
import {
  MAX_LOOP_CROSSFADE_SEC,
  describeZoneRange,
  midiNoteName,
  readSamplerZones,
  usableCrossfade,
  type SamplerZone,
} from '@/engine/instruments';
import { AUDIO_ACCEPT, pickFiles } from '@/storage/fileIO';
import { getServices } from '@/state/services';
import { useDaw } from '@/state/store';
import { ZoneWaveform } from './ZoneWaveform';

/**
 * The sampler's key map.
 *
 * Zones are listed rather than drawn on a keyboard graphic: at the sizes this
 * panel gets, a list shows the root note, the range and the settings all at
 * once, where a graphic would show the ranges and hide everything else behind a
 * selection.
 */
export function SamplerZones({ track }: { track: Track }): JSX.Element {
  const dispatch = useDaw((s) => s.dispatch);
  const [dragOver, setDragOver] = useState(false);
  const [busy, setBusy] = useState(false);

  const zones = readSamplerZones(track.instrument?.data);

  const load = async (files: File[]): Promise<void> => {
    if (files.length === 0) return;
    setBusy(true);
    try {
      await useDaw.getState().importSamplerFiles(files, track.id);
    } finally {
      setBusy(false);
    }
  };

  const browse = async (): Promise<void> => {
    try {
      await load(await pickFiles('Audio files', AUDIO_ACCEPT, true));
    } catch {
      /* dialog dismissed */
    }
  };

  return (
    <div className="inspector__group" style={{ gridColumn: '1 / -1' }}>
      <div className="inspector__title">
        Sampler — {zones.length === 0 ? 'no samples' : `${zones.length} zone${zones.length === 1 ? '' : 's'}`}
      </div>

      <div
        className={`dropwell ${dragOver ? 'dropwell--over' : ''}`}
        onDragOver={(e) => {
          e.preventDefault();
          setDragOver(true);
        }}
        onDragLeave={() => setDragOver(false)}
        onDrop={(e) => {
          e.preventDefault();
          setDragOver(false);
          void load(
            [...e.dataTransfer.files].filter(
              (f) => f.type.startsWith('audio/') || /\.(wav|mp3|m4a|aac|ogg|oga|flac)$/i.test(f.name),
            ),
          );
        }}
      >
        <span>
          {busy
            ? 'Loading samples…'
            : 'Drop audio here to map it across the keyboard. Root notes are read from file names like “Piano_C3.wav”.'}
        </span>
        <div style={{ display: 'flex', gap: 6 }}>
          <button className="btn" onClick={() => void browse()} disabled={busy}>
            Add samples…
          </button>
          <button
            className="btn"
            disabled={zones.length < 2}
            onClick={() => dispatch(command(SamplerCommands.autoMapSampler, { trackId: track.id }))}
            title="Give every zone the keys nearest its root note"
          >
            Spread across keyboard
          </button>
        </div>
      </div>

      {zones.map((zone) => (
        <div className="zone" key={zone.id}>
          <div className="zone__head">
            <span className="zone__name" title={zone.name}>
              {zone.name}
            </span>
            <span className="zone__range">{describeZoneRange(zone)}</span>
            <button
              className="chip"
              onClick={() => dispatch(command(SamplerCommands.removeSamplerZone, { trackId: track.id, zoneId: zone.id }))}
              aria-label={`Remove ${zone.name}`}
              title="Remove this zone"
            >
              ✕
            </button>
          </div>

          <div className="zone__controls">
            <label className="zone__field">
              <span className="field__label">Root</span>
              <input
                className="numeric"
                type="number"
                min={0}
                max={127}
                value={zone.rootPitch}
                aria-label={`${zone.name} root note`}
                onChange={(e) =>
                  dispatch({
                    type: SamplerCommands.updateSamplerZone.type,
                    params: { trackId: track.id, zoneId: zone.id, rootPitch: Number(e.target.value) },
                  })
                }
              />
              <span className="zone__note">{midiNoteName(zone.rootPitch)}</span>
            </label>

            <label className="zone__field">
              <span className="field__label">Low</span>
              <input
                className="numeric"
                type="number"
                min={0}
                max={127}
                value={zone.lowPitch}
                aria-label={`${zone.name} lowest key`}
                onChange={(e) =>
                  dispatch({
                    type: SamplerCommands.updateSamplerZone.type,
                    params: { trackId: track.id, zoneId: zone.id, lowPitch: Number(e.target.value) },
                  })
                }
              />
            </label>

            <label className="zone__field">
              <span className="field__label">High</span>
              <input
                className="numeric"
                type="number"
                min={0}
                max={127}
                value={zone.highPitch}
                aria-label={`${zone.name} highest key`}
                onChange={(e) =>
                  dispatch({
                    type: SamplerCommands.updateSamplerZone.type,
                    params: { trackId: track.id, zoneId: zone.id, highPitch: Number(e.target.value) },
                  })
                }
              />
            </label>

            <ZoneWaveform trackId={track.id} zone={zone} />

            <label className="zone__field">
              <span className="field__label">Vel</span>
              <input
                className="numeric"
                type="number"
                min={1}
                max={127}
                value={zone.lowVelocity}
                aria-label={`${zone.name} lowest velocity`}
                title="The softest note this zone answers to. Layer two recordings of the same key to make a hard hit a different sound rather than a louder one."
                onChange={(e) =>
                  dispatch({
                    type: SamplerCommands.updateSamplerZone.type,
                    params: { trackId: track.id, zoneId: zone.id, lowVelocity: Number(e.target.value) },
                  })
                }
              />
            </label>

            <label className="zone__field">
              <span className="field__label">to</span>
              <input
                className="numeric"
                type="number"
                min={1}
                max={127}
                value={zone.highVelocity}
                aria-label={`${zone.name} highest velocity`}
                onChange={(e) =>
                  dispatch({
                    type: SamplerCommands.updateSamplerZone.type,
                    params: { trackId: track.id, zoneId: zone.id, highVelocity: Number(e.target.value) },
                  })
                }
              />
            </label>

            <label className="zone__field">
              <span className="field__label">Robin</span>
              <input
                className="numeric"
                type="number"
                min={0}
                max={64}
                value={zone.roundRobinGroup}
                aria-label={`${zone.name} round robin group`}
                title="Zones sharing a group take turns instead of sounding together. Zero means this zone always plays."
                onChange={(e) =>
                  dispatch({
                    type: SamplerCommands.updateSamplerZone.type,
                    params: { trackId: track.id, zoneId: zone.id, roundRobinGroup: Number(e.target.value) },
                  })
                }
              />
            </label>

            <label className="zone__field">
              <span className="field__label">Choke</span>
              <input
                className="numeric"
                type="number"
                min={0}
                max={64}
                value={zone.chokeGroup}
                aria-label={`${zone.name} choke group`}
                title="Zones sharing a choke group cut each other off: the moment one starts, whatever the group has sounding stops — the closed hat silencing the open one. Zero means this zone chokes nothing and is choked by nothing."
                onChange={(e) =>
                  dispatch({
                    type: SamplerCommands.updateSamplerZone.type,
                    params: { trackId: track.id, zoneId: zone.id, chokeGroup: Number(e.target.value) },
                  })
                }
              />
            </label>

            <label className="zone__field zone__field--wide">
              <span className="field__label">Gain</span>
              <input
                className="slider"
                type="range"
                min={0}
                max={2}
                step={0.01}
                value={zone.gain}
                aria-label={`${zone.name} gain`}
                onChange={(e) =>
                  dispatch({
                    type: SamplerCommands.updateSamplerZone.type,
                    params: { trackId: track.id, zoneId: zone.id, gain: Number(e.target.value) },
                  })
                }
                onPointerUp={() => getServices().commands.breakCoalescing()}
              />
            </label>

            <label className="zone__field zone__field--wide">
              <span className="field__label">Tune</span>
              <input
                className="slider"
                type="range"
                min={-100}
                max={100}
                step={1}
                value={zone.tuneCents}
                aria-label={`${zone.name} tuning`}
                onChange={(e) =>
                  dispatch({
                    type: SamplerCommands.updateSamplerZone.type,
                    params: { trackId: track.id, zoneId: zone.id, tuneCents: Number(e.target.value) },
                  })
                }
                onPointerUp={() => getServices().commands.breakCoalescing()}
              />
              <span className="zone__note">{zone.tuneCents > 0 ? `+${zone.tuneCents}` : zone.tuneCents}c</span>
            </label>

            <button
              className={`chip ${zone.loop ? 'chip--mon chip--on' : ''}`}
              onClick={() =>
                dispatch({
                  type: SamplerCommands.updateSamplerZone.type,
                  params: { trackId: track.id, zoneId: zone.id, loop: !zone.loop },
                })
              }
              aria-pressed={zone.loop}
              title="Hold the sample for as long as the note is held, looping between its loop points — the whole region until the amber marks on the waveform are moved"
            >
              LOOP
            </button>
            {zone.loop && (zone.loopStartSec > zone.offsetSec || zone.loopEndSec < zone.offsetSec + zone.durationSec) && (
              <span
                className="zone__note"
                title="Where the loop turns around; the attack before it plays once, and what follows it plays on release"
              >
                loop {zone.loopStartSec.toFixed(3)}–{zone.loopEndSec.toFixed(3)} s
              </span>
            )}
            {zone.loop && (
              <label className="zone__field" title={crossfadeTitle(zone)}>
                <span className="field__label">Xfade</span>
                <input
                  className="numeric"
                  type="number"
                  min={0}
                  max={MAX_LOOP_CROSSFADE_SEC * 1000}
                  step={1}
                  value={Math.round(zone.loopCrossfadeSec * 1000)}
                  aria-label={`${zone.name} loop crossfade`}
                  onChange={(e) =>
                    dispatch({
                      type: SamplerCommands.updateSamplerZone.type,
                      params: { trackId: track.id, zoneId: zone.id, loopCrossfadeSec: Number(e.target.value) / 1000 },
                    })
                  }
                />
                <span className="zone__note">ms</span>
              </label>
            )}
            <button
              className={`chip ${zone.keyTracking ? 'chip--mon chip--on' : ''}`}
              onClick={() =>
                dispatch({
                  type: SamplerCommands.updateSamplerZone.type,
                  params: { trackId: track.id, zoneId: zone.id, keyTracking: !zone.keyTracking },
                })
              }
              aria-pressed={zone.keyTracking}
              title="Follow the keyboard: each key is transposed by its distance from the root note. Off, every key in the range plays the sample as recorded — for a drum laid across keys."
            >
              KEY
            </button>
            <button
              className={`chip ${zone.oneShot ? 'chip--mon chip--on' : ''}`}
              onClick={() =>
                dispatch({
                  type: SamplerCommands.updateSamplerZone.type,
                  params: { trackId: track.id, zoneId: zone.id, oneShot: !zone.oneShot },
                })
              }
              aria-pressed={zone.oneShot}
              title="Play the sample out to the end of its region however short the note — a cymbal rings on after a short hit. Turns looping off."
            >
              1SHOT
            </button>
            <button
              className={`chip ${zone.reverse ? 'chip--mon chip--on' : ''}`}
              onClick={() =>
                dispatch({
                  type: SamplerCommands.updateSamplerZone.type,
                  params: { trackId: track.id, zoneId: zone.id, reverse: !zone.reverse },
                })
              }
              aria-pressed={zone.reverse}
              title="Play the sample backwards"
            >
              REV
            </button>
          </div>
        </div>
      ))}
    </div>
  );
}

/** What the crossfade field can and cannot do for this zone, in its title. */
function crossfadeTitle(zone: SamplerZone): string {
  const usable = usableCrossfade(zone.loopStartSec, zone.loopEndSec, zone.loopCrossfadeSec);
  const base = 'How long the end of the loop is blended into its start, so the join does not click';
  if (zone.loopCrossfadeSec > 0 && usable <= 0) {
    return `${base}. Nothing to blend with: the loop starts at the very start of the file, so this has no effect until the loop's start is moved in.`;
  }
  if (usable < zone.loopCrossfadeSec - 1e-9) {
    return `${base}. Held to ${Math.round(usable * 1000)}ms here: no longer than what leads into the loop, and no more than half the loop.`;
  }
  return base;
}
